import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { UserRole } from '@/types/auth';
import { Sidebar } from '@/components/common/Sidebar';
import { ThemeToggle } from '@/components/common';
import {
  Calendar,
  Clock,
  User,
  Search,
  Bell,
  Loader2,
  AlertCircle,
  CheckCircle,
  XCircle,
  Edit,
  MapPin,
  Navigation,
  MessageCircle,
  Check,
  X,
  UserCheck,
  UserX,
  Flag,
} from 'lucide-react';
import { 
  getAppointmentsByDoctor, 
  updateAppointment, 
  updateDoctorLocation, 
  endDoctorTracking,
  acceptAppointment,
  refuseAppointment,
  approveReschedule,
  rejectReschedule
} from '@/services/index';
import { GlobalSearch, NotificationCenter } from '@/components/dashboard/HeaderActions';
import type { AppointmentDTO } from '@/services/index';
import { ReportModal } from '@/components/common/ReportModal';
import { AppointmentClosureModal } from '@/components/consultation/AppointmentClosureModal';
import { formatAppointmentReasonForDisplay } from '@/lib/appointmentReason';

export default function ConsultasPage() {
  const navigate = useNavigate();
  const { user, isLoading, logout } = useAuth();
  const [isLoadingAppointments, setIsLoadingAppointments] = useState(false);
  const [appointments, setAppointments] = useState<AppointmentDTO[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [trackingId, setTrackingId] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState<string>('');
  const [reportData, setReportData] = useState<{ id: string, name: string } | null>(null);
  const [closureModalData, setClosureModalData] = useState<string | null>(null);

  useEffect(() => {
    if (!isLoading && !user) {
      navigate('/login');
    } else if (!isLoading && user && user.role !== UserRole.DOCTOR) {
      navigate('/dashboard');
    }
  }, [user, isLoading, navigate]);

  const loadAppointments = async () => {
    if (!user || user.role !== UserRole.DOCTOR) return;
    setIsLoadingAppointments(true);
    setError(null);

    try {
      const response = await getAppointmentsByDoctor(user.id);
      // Ensure response.data is always an array
      const appointmentsData = Array.isArray(response.data) ? response.data : [];
      setAppointments(appointmentsData);
    } catch (err) {
      console.error('Error loading appointments:', err);
      setError('Erro ao carregar suas consultas');
      setAppointments([]);
    } finally {
      setIsLoadingAppointments(false);
    }
  };

  useEffect(() => {
    loadAppointments();
  }, [user]);

  // Initial check for in transit appointments
  useEffect(() => {
    const transitApp = (appointments || []).find(a => a.status === 'in_transit');
    if (transitApp) setTrackingId(transitApp.id);
  }, [appointments]);

  // Tracking loop
  useEffect(() => {
    let watchId: number;
    if (trackingId) {
      if ('geolocation' in navigator) {
        watchId = navigator.geolocation.watchPosition(
          (position) => {
            updateDoctorLocation(trackingId, {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            }).catch((err) => console.error('Error sending location', err));
          },
          (err) => console.error('Geolocation error', err),
          { enableHighAccuracy: true, maximumAge: 10000, timeout: 5000 }
        );
      } else {
        console.warn('Geolocation not supported');
      }
    }
    return () => {
      if (watchId !== undefined) navigator.geolocation.clearWatch(watchId);
    };
  }, [trackingId]);

  const handleAccept = async (id: string) => {
    setActionLoading(id);
    try {
      const res = await acceptAppointment(id);
      if (res && res.success === false) {
        toast.error(res.error || 'Erro ao aceitar consulta');
        return;
      }
      toast.success('Consulta aceita com sucesso!');
      loadAppointments();
    } catch (err) {
      toast.error('Erro ao aceitar consulta');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRefuse = async (id: string) => {
    setActionLoading(id);
    try {
      const res = await refuseAppointment(id);
      if (res && res.success === false) {
        toast.error(res.error || 'Erro ao recusar consulta');
        return;
      }
      toast.success('Consulta recusada.');
      loadAppointments();
    } catch (err) {
      toast.error('Erro ao recusar consulta');
    } finally {
      setActionLoading(null);
    }
  };

  const handleApproveReschedule = async (id: string) => {
    setActionLoading(id);
    try {
      const res = await approveReschedule(id);
      if (res && res.success === false) {
        toast.error(res.error || 'Erro ao aprovar reagendamento');
        return;
      }
      toast.success('Reagendamento aprovado com sucesso');
      loadAppointments();
    } catch (error) {
      toast.error('Erro ao aprovar reagendamento');
      console.error(error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectReschedule = async (id: string) => {
    setActionLoading(id);
    try {
      const res = await rejectReschedule(id);
      if (res && res.success === false) {
        toast.error(res.error || 'Erro ao rejeitar reagendamento');
        return;
      }
      toast.success('Reagendamento rejeitado');
      loadAppointments();
    } catch (error) {
      toast.error('Erro ao rejeitar reagendamento');
      console.error(error);
    } finally {
      setActionLoading(null);
    }
  };

  const isDomiciliar = (app: AppointmentDTO) => {
    if (app.type === 'video' || app.appointmentType === 'TELEMEDICINA') return false;
    if (app.type === 'clinic' || app.appointmentType === 'CONSULTORIO') return false;
    return app.type === 'home' || app.appointmentType === 'DOMICILIAR' || app.appointmentType === 'PLANTAO';
  };

  const handleStartTransit = async (id: string) => {
    setActionLoading(id);
    try {
      const res = await updateAppointment(id, { status: 'in_transit' });
      if (res && (res as any).success === false) {
        toast.error((res as any).error || 'Erro ao iniciar deslocamento');
        return;
      }
      setTrackingId(id);
      setAppointments(prev => (prev || []).map(a => a.id === id ? { ...a, status: 'in_transit' as any } : a));
      toast.success('Deslocamento iniciado com sucesso!');
      setAnnouncement('Status da consulta alterado para: Em Deslocamento. Iniciando rastreamento de localização.');
    } catch (err: any) {
      const errorMsg = err?.message || err?.error || 'Erro ao iniciar deslocamento';
      toast.error(errorMsg);
      setAnnouncement('Erro ao iniciar deslocamento.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleStopTransit = async (id: string, newStatus: 'scheduled' | 'completed' = 'scheduled') => {
    try {
      await updateAppointment(id, { status: newStatus });
      await endDoctorTracking(id).catch(console.error);
      setTrackingId(null);
      setAppointments(prev => (prev || []).map(a => a.id === id ? { ...a, status: newStatus as any } : a));
      
      const newStatusLabel = getStatusLabel(newStatus).label;
      setAnnouncement(`Status da consulta alterado para: ${newStatusLabel}. Rastreamento finalizado.`);
    } catch (err) {
      console.error(err);
      setError('Erro ao finalizar deslocamento');
      setAnnouncement('Erro ao finalizar deslocamento.');
    }
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, { label: string; color: string; icon: any }> = {
      requested: {
        label: 'Solicitada',
        color: 'amber',
        icon: Clock,
      },
      scheduled: {
        label: 'Agendada',
        color: 'blue',
        icon: CheckCircle,
      },
      reschedule_requested: {
        label: 'Reagendamento Solicitado',
        color: 'purple',
        icon: Clock,
      },
      completed: {
        label: 'Realizada',
        color: 'green',
        icon: CheckCircle,
      },
      cancelled: {
        label: 'Cancelada/Recusada',
        color: 'red',
        icon: XCircle,
      },
      'no-show': {
        label: 'Não Compareceu',
        color: 'amber',
        icon: AlertCircle,
      },
      in_transit: {
        label: 'Em Deslocamento',
        color: 'indigo',
        icon: Navigation,
      },
    };
    return labels[status] || labels.scheduled;
  };

  const calculateAge = (dateOfBirth?: string) => {
    if (!dateOfBirth) return null;
    const birthDate = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const formatAppointmentDateTime = (appointmentDate: string | Date) => {
    const date = new Date(appointmentDate);
    if (isNaN(date.getTime())) {
      return { date: 'Data inválida', time: 'Horário inválido' };
    }
    
    const dateStr = date.toLocaleDateString('pt-BR');
    const timeStr = date.toLocaleTimeString('pt-BR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
    
    return { date: dateStr, time: timeStr };
  };

  const pendingRequests = (appointments || []).filter(a => 
    a.status === 'requested' || a.status === 'reschedule_requested'
  );
  
  const activeAppointments = (appointments || []).filter(a => 
    ['scheduled', 'in_transit'].includes(a.status)
  );

  const pastAppointments = (appointments || []).filter(a => 
    ['completed', 'cancelled', 'no-show'].includes(a.status)
  );

  if (isLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-neutral-950">
        <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950 flex">
      <div aria-live="polite" className="sr-only" role="status">
        {announcement}
      </div>

      <Sidebar
        userRole={user.role}
        userName={user.name}
        onLogout={async () => navigate('/login')}
      />

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-800 px-8 py-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Gestão de Consultas
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Aprovação de solicitações e acompanhamento de agendamentos
            </p>
          </div>

          <div className="flex items-center gap-4">
            <GlobalSearch />
            <NotificationCenter />
            <ThemeToggle />
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-8 bg-gray-50 dark:bg-neutral-950">
          {error && (
            <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
          )}

          <div className="space-y-12 max-w-6xl mx-auto">
            {/* INBOX: Solicitações Pendentes */}
            <section>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <Bell className="w-5 h-5 text-amber-500" />
                  Solicitações Pendentes (Inbox)
                  {pendingRequests.length > 0 && (
                    <span className="ml-2 px-2.5 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-xs font-bold rounded-full">
                      {pendingRequests.length}
                    </span>
                  )}
                </h2>
              </div>

              {isLoadingAppointments ? (
                <div className="flex justify-center py-12"><Loader2 className="animate-spin text-teal-600" /></div>
              ) : pendingRequests.length === 0 ? (
                <div className="bg-white dark:bg-neutral-900 rounded-2xl p-10 text-center border border-gray-200 dark:border-neutral-800 border-dashed">
                  <p className="text-gray-500">Nenhuma solicitação pendente no momento.</p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {pendingRequests.map((app) => (
                    <div key={app.id} className="bg-white dark:bg-neutral-900 rounded-2xl p-6 border border-gray-200 dark:border-neutral-800 shadow-sm hover:shadow-md transition-all flex flex-col md:flex-row gap-6">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-neutral-800 flex items-center justify-center text-gray-400">
                          <User className="w-8 h-8" />
                        </div>
                      </div>
                      
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-3">
                          <h3 className="font-bold text-gray-900 dark:text-white">
                            {app.patient?.user?.name || 'Paciente'}
                          </h3>
                          {app.patient?.patientProfile?.dateOfBirth && (
                            <span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-neutral-800 text-gray-600 dark:text-gray-400 rounded-full">
                              {calculateAge(app.patient.patientProfile.dateOfBirth)} anos
                            </span>
                          )}
                        </div>
                        
                        <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-500">
                          {app.status === 'reschedule_requested' && app.rescheduleRequestedDate ? (
                            <div className="flex flex-col gap-1 w-full mb-2 bg-amber-50 dark:bg-amber-900/10 p-3 rounded-xl border border-amber-100 dark:border-amber-900/30">
                              <span className="text-xs font-bold text-amber-800 dark:text-amber-400">Sugestão de Reagendamento:</span>
                              <div className="flex items-center gap-4">
                                <div className="flex items-center gap-1.5"><Calendar className="w-4 h-4 text-amber-600" /> {new Date(app.rescheduleRequestedDate).toLocaleDateString('pt-BR')}</div>
                                <div className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-amber-600" /> {new Date(app.rescheduleRequestedDate).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</div>
                              </div>
                              <span className="text-xs font-bold text-gray-500 mt-2">Data Original:</span>
                              <div className="flex items-center gap-4 line-through opacity-70">
                                <div className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {new Date(app.appointmentDate).toLocaleDateString('pt-BR')}</div>
                                <div className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {new Date(app.appointmentDate).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</div>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="flex items-center gap-1.5"><Calendar className="w-4 h-4 text-teal-600" /> {new Date(app.appointmentDate).toLocaleDateString('pt-BR')}</div>
                              <div className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-teal-600" /> {new Date(app.appointmentDate).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</div>
                            </>
                          )}
                          {app.patient?.patientProfile && (
                            <div className="flex items-center gap-1.5 mt-1"><MapPin className="w-4 h-4 text-teal-600" /> {app.patient.patientProfile.street}, {app.patient.patientProfile.number} - {app.patient.patientProfile.city}</div>
                          )}
                        </div>
                        <p className="text-sm border-l-2 border-teal-500/30 pl-3 mt-3 italic">
                          "{formatAppointmentReasonForDisplay(app.reason, 'Sem motivo informado')}"
                        </p>
                      </div>

                      <div className="flex items-center gap-3">
                        {app.status === 'reschedule_requested' ? (
                          <>
                            <button
                              onClick={() => handleRejectReschedule(app.id)}
                              disabled={actionLoading === app.id}
                              className="flex-1 md:flex-none inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400 font-bold rounded-xl hover:bg-rose-100 transition-colors disabled:opacity-50"
                            >
                              {actionLoading === app.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <><UserX className="w-4 h-4" /> Rejeitar remarcação</>
                              )}
                            </button>
                            <button
                              onClick={() => handleApproveReschedule(app.id)}
                              disabled={actionLoading === app.id}
                              className="flex-1 md:flex-none inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-teal-600 text-white font-bold rounded-xl hover:bg-teal-700 shadow-lg shadow-teal-600/20 transition-all disabled:opacity-50"
                            >
                              {actionLoading === app.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <><UserCheck className="w-4 h-4" /> Aprovar remarcação</>
                              )}
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => handleRefuse(app.id)}
                              disabled={actionLoading === app.id}
                              className="flex-1 md:flex-none inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400 font-bold rounded-xl hover:bg-rose-100 transition-colors disabled:opacity-50"
                            >
                              {actionLoading === app.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <><UserX className="w-4 h-4" /> Recusar</>
                              )}
                            </button>
                            <button
                              onClick={() => handleAccept(app.id)}
                              disabled={actionLoading === app.id}
                              className="flex-1 md:flex-none inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-teal-600 text-white font-bold rounded-xl hover:bg-teal-700 shadow-lg shadow-teal-600/20 transition-all disabled:opacity-50"
                            >
                              {actionLoading === app.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <><UserCheck className="w-4 h-4" /> Aceitar</>
                              )}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* LISTA: Agendamentos Ativos */}
            <section>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-teal-500" />
                  Próximos Agendamentos
                </h2>
                <button 
                  onClick={() => navigate('/agenda')}
                  className="text-sm font-semibold text-teal-600 hover:text-teal-700 flex items-center gap-1"
                >
                  <Edit className="w-4 h-4" /> Gerenciar Agenda
                </button>
              </div>

              {activeAppointments.length === 0 ? (
                <div className="bg-white dark:bg-neutral-900 rounded-2xl p-10 text-center border border-gray-200 dark:border-neutral-800">
                  <p className="text-gray-500">Sem agendamentos no momento.</p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {activeAppointments.map((app) => {
                    const status = getStatusLabel(app.status);
                    const StatusIcon = status.icon;
                    return (
                      <div key={app.id} className="bg-white dark:bg-neutral-900 rounded-2xl p-6 border border-gray-200 dark:border-neutral-800 hover:border-teal-500/50 transition-all">
                        <div className="flex flex-col md:flex-row justify-between gap-4">
                          <div className="flex gap-4">
                            <div className="w-12 h-12 rounded-xl bg-teal-50 dark:bg-teal-900/10 flex items-center justify-center text-teal-600">
                              <User className="w-6 h-6" />
                            </div>
                            <div>
                              <h3 className="font-bold text-gray-900 dark:text-white">{app.patient?.user?.name || 'Paciente'}</h3>
                              <div className="flex gap-3 text-xs text-gray-500 mt-1">
                                <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(app.appointmentDate).toLocaleDateString('pt-BR')}</span>
                                <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(app.appointmentDate).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-3">
                            <div className={`px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1.5
                              ${status.color === 'blue' ? 'bg-blue-100 text-blue-600' : ''}
                              ${status.color === 'green' ? 'bg-green-100 text-green-600' : ''}
                              ${status.color === 'amber' ? 'bg-amber-100 text-amber-600' : ''}
                              ${status.color === 'red' ? 'bg-red-100 text-red-600' : ''}
                              ${status.color === 'indigo' ? 'bg-indigo-100 text-indigo-600' : ''}
                            `}>
                              <StatusIcon className="w-3.5 h-3.5" /> {status.label}
                            </div>
                            
                            {app.status === 'scheduled' && (
                              <div className="flex gap-2 flex-wrap items-center">
                                <button
                                  onClick={() => setReportData({ id: app.patientId, name: app.patient?.user?.name || 'Paciente' })}
                                  className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                                  title="Denunciar Paciente"
                                >
                                  <Flag className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => navigate(`/chat/${app.id}`)}
                                  className="p-2 bg-gray-100 dark:bg-neutral-800 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                                  title="Sala / documentos"
                                >
                                  <MessageCircle className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => navigate(`/chat/${app.id}`)}
                                  className="px-3 py-1.5 bg-teal-600 text-white text-xs font-bold rounded-lg hover:bg-teal-700 transition-colors"
                                  title="Emitir atestado, receita ou prescrição"
                                >
                                  Documentos
                                </button>
                                {/* Localização / tipo para consultas não-domiciliares */}
                                {!isDomiciliar(app) && (app.type === 'clinic' || app.appointmentType === 'CONSULTORIO') && app.patient?.patientProfile && (
                                  <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 text-xs font-semibold rounded-lg border border-blue-200 dark:border-blue-800">
                                    <MapPin className="w-3.5 h-3.5" />
                                    {app.patient.patientProfile.street
                                      ? `${app.patient.patientProfile.street}${app.patient.patientProfile.number ? ', ' + app.patient.patientProfile.number : ''} – ${app.patient.patientProfile.city || ''}`
                                      : 'Endereço do paciente'}
                                  </div>
                                )}
                                {!isDomiciliar(app) && (app.type === 'video' || app.appointmentType === 'TELEMEDICINA') && (
                                  app.videoCallUrl ? (
                                    <a
                                      href={app.videoCallUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="px-3 py-1.5 bg-violet-600 text-white text-xs font-bold rounded-lg hover:bg-violet-700 transition-colors flex items-center gap-1"
                                    >
                                      <Navigation className="w-3.5 h-3.5" /> Entrar na Chamada
                                    </a>
                                  ) : (
                                    <span className="px-3 py-1.5 bg-violet-50 dark:bg-violet-900/20 text-violet-600 dark:text-violet-300 text-xs font-semibold rounded-lg border border-violet-200 dark:border-violet-800">
                                      Telemedicina
                                    </span>
                                  )
                                )}
                                <button onClick={() => setClosureModalData(app.id)} className="px-3 py-1.5 bg-green-600 text-white text-xs font-bold rounded-lg hover:bg-green-700 transition-colors">Concluir Consulta</button>
                                {/* Botão Iniciar Visita: apenas para consultas domiciliares */}
                                {isDomiciliar(app) && (
                                  <button
                                    onClick={() => handleStartTransit(app.id)}
                                    disabled={actionLoading === app.id}
                                    className="px-3 py-1.5 bg-indigo-600 text-white text-xs font-bold rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center gap-1"
                                  >
                                    {actionLoading === app.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Navigation className="w-3 h-3" /> Iniciar Visita</>}
                                  </button>
                                )}
                              </div>
                            )}
                            {app.status === 'reschedule_requested' && (
                              <div className="flex gap-2">
                                <button 
                                  onClick={() => handleRejectReschedule(app.id)}
                                  disabled={actionLoading === app.id}
                                  className="px-3 py-1.5 bg-rose-600 text-white text-xs font-bold rounded-lg hover:bg-rose-700 transition-colors disabled:opacity-50 flex items-center gap-1"
                                >
                                  {actionLoading === app.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Rejeitar'}
                                </button>
                                <button 
                                  onClick={() => handleApproveReschedule(app.id)}
                                  disabled={actionLoading === app.id}
                                  className="px-3 py-1.5 bg-green-600 text-white text-xs font-bold rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-1"
                                >
                                  {actionLoading === app.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Aprovar'}
                                </button>
                              </div>
                            )}
                            {app.status === 'in_transit' && (
                              <button onClick={() => setClosureModalData(app.id)} className="px-3 py-1.5 bg-green-600 text-white text-xs font-bold rounded-lg hover:bg-green-700 transition-colors">Finalizar Visita</button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </div>
        </main>

        {reportData && user && (
          <ReportModal
            reportedId={reportData.id}
            reportedName={reportData.name}
            onClose={() => setReportData(null)}
          />
        )}

        <AppointmentClosureModal
          isOpen={!!closureModalData}
          onClose={() => setClosureModalData(null)}
          appointmentId={closureModalData || ''}
          evolutionContent="Consulta concluída via gestor de consultas."
          onSuccess={() => {
            loadAppointments();
            if (trackingId && trackingId === closureModalData) {
               endDoctorTracking(closureModalData).catch(console.error);
               setTrackingId(null);
            }
          }}
        />
      </div>
    </div>
  );
}
